import { OpenInNew as OpenInNewIcon } from "@mui/icons-material";
import type React from "react";
import DocumentPreview from "~/components/Document/documentPreview";
import styles from "./index.module.scss";

type DocumentStatus = "ready" | "not-ready" | "generating";

export interface DocumentCardProps {
  title: string;
  icon: React.ReactNode;
  status: DocumentStatus;
  modifiedAt?: string | undefined;
  content?: string | undefined;
  documentType: "app-design" | "field-design" | "customize-specification";
  documentUrl?: string | undefined;
  versionInfo?: string | undefined;
  onClick?: () => void;
  /** 別タブで開くアイコンを表示するか */
  showOpenInNewIcon?: boolean;
}

const formatDate = (dateString?: string): string => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}/${month}/${day}`;
};

const DocumentCard: React.FC<DocumentCardProps> = ({
  title,
  icon,
  status,
  modifiedAt,
  content,
  documentType,
  documentUrl,
  versionInfo,
  onClick,
  showOpenInNewIcon = false,
}) => {
  const getStatusLabel = () => {
    if (status === "generating") return "生成中";
    if (status === "not-ready") return "未作成";
    return null;
  };

  const statusLabel = getStatusLabel();
  const formattedDate = formatDate(modifiedAt);
  const isClickable = status === "ready" && onClick;

  return (
    <button
      type="button"
      className={`${styles["documentCard"]} ${isClickable ? styles["clickable"] : ""} ${status !== "ready" ? styles["disabled"] : ""}`}
      onClick={isClickable ? onClick : undefined}
      disabled={!isClickable}
    >
      <div className={styles["cardHeader"]}>
        <div className={styles["cardTitleRow"]}>
          <span className={styles["cardIcon"]}>{icon}</span>
          <span className={styles["cardTitle"]}>{title}</span>
          {showOpenInNewIcon && status === "ready" && (
            <OpenInNewIcon className={styles["openInNewIcon"] ?? ""} />
          )}
        </div>
        <div className={styles["cardMeta"]}>
          {status === "ready" && formattedDate && (
            <span className={styles["cardDate"]}>{formattedDate} 更新</span>
          )}
          {status === "ready" && versionInfo && (
            <span className={styles["versionInfo"]}>{versionInfo}</span>
          )}
          {statusLabel && (
            <span className={`${styles["statusLabel"]} ${styles[status]}`}>{statusLabel}</span>
          )}
        </div>
      </div>
      <div className={styles["cardContent"]}>
        <DocumentPreview
          content={status === "ready" ? content : undefined}
          documentType={documentType}
          documentUrl={documentUrl}
          previewMode="css"
          height={200}
        />
      </div>
    </button>
  );
};

export default DocumentCard;
